import { FC, useCallback, useEffect, useState } from "react";

import {
  CheckCircle,
  Cancel,
  LocationOn,
  Mic,
  Videocam,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ConnectInvitationModel,
  getConnectInvitations,
  postConnectInvitationDecision,
} from "@rubi/api-generated/clientRs";
import { toast } from "@rubi/ui";
import { useTranslation } from "react-i18next";

import { consoleMsgFrom } from "./apiError";
import ParticipantLocationsMap from "./ParticipantLocationsMap";

interface Props {
  open: boolean;
  onClose: () => void;
  orgId: string;
  meetingId: string | null;
  meetingTitle?: string;
}

const POLL_MS = 5000;
const STATUS_KEYS: Record<string, string> = {
  invited: "waitingRoom.status.invited",
  link_opened: "waitingRoom.status.linkOpened",
  permissions_pending: "waitingRoom.status.permissionsPending",
  waiting_room: "waitingRoom.status.waitingRoom",
  admitted: "waitingRoom.status.admitted",
  removed: "waitingRoom.status.removed",
  left: "waitingRoom.status.left",
  reconnected: "waitingRoom.status.reconnected",
};

const ReadyIcon: FC<{ ok: boolean; label: string; icon: React.ReactNode }> = ({
  ok,
  label,
  icon,
}) => {
  const { t } = useTranslation("connect");
  return (
    <Tooltip
      title={
        ok
          ? t("waitingRoom.readiness.granted", { label })
          : t("waitingRoom.readiness.notGranted", { label })
      }
    >
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 0.25,
          color: ok ? "success.main" : "text.disabled",
        }}
      >
        {icon}
        {ok ? (
          <CheckCircle sx={{ fontSize: 14 }} />
        ) : (
          <Cancel sx={{ fontSize: 14 }} />
        )}
      </Box>
    </Tooltip>
  );
};

const WaitingRoomDialog: FC<Props> = ({
  open,
  onClose,
  orgId,
  meetingId,
  meetingTitle,
}) => {
  const { t } = useTranslation("connect");
  const [invitations, setInvitations] = useState<ConnectInvitationModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!meetingId) return;
    try {
      const res = await getConnectInvitations({
        path: { org_id: orgId, meeting_id: meetingId },
      });
      setInvitations(res.data?.invitations ?? []);
    } catch {
      /* transient poll failure — keep prior list */
    }
  }, [orgId, meetingId]);

  // Initial load + periodic refresh while open.
  useEffect(() => {
    if (!open || !meetingId) return;
    let cancelled = false;
    setLoading(true);
    load().finally(() => {
      if (!cancelled) setLoading(false);
    });
    const id = setInterval(load, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [open, meetingId, load]);

  const decide = async (
    invitationId: string,
    action: "admit" | "decline" | "remove",
  ) => {
    if (!meetingId) return;
    setActing(invitationId + action);
    try {
      await postConnectInvitationDecision({
        path: {
          org_id: orgId,
          meeting_id: meetingId,
          invitation_id: invitationId,
        },
        body: { action },
      });
      toast.success(
        action === "admit"
          ? t("waitingRoom.toast.admitted")
          : t("waitingRoom.toast.removed"),
      );
      await load();
    } catch (e: unknown) {
      toast.error(consoleMsgFrom(e) ?? t("waitingRoom.toast.actionFailed"));
    } finally {
      setActing(null);
    }
  };

  const located = invitations
    .filter((i) => i.latitude != null && i.longitude != null)
    .map((i) => ({
      id: i.id,
      name: i.name,
      lat: i.latitude as number,
      lng: i.longitude as number,
    }));

  const fmtTime = (iso?: string | null) =>
    iso ? new Date(iso).toLocaleTimeString() : "—";

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {meetingTitle
          ? t("waitingRoom.titleWithMeeting", { title: meetingTitle })
          : t("waitingRoom.title")}
      </DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary">
          {t("waitingRoom.description")}
        </Typography>

        {loading && invitations.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : invitations.length === 0 ? (
          <Typography color="text.secondary" variant="body2" sx={{ mt: 2 }}>
            {t("waitingRoom.empty")}
          </Typography>
        ) : (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {invitations.map((inv) => {
              const admitted = inv.status === "admitted";
              const removed = inv.status === "removed";
              // Only someone who actually reached the waiting room (mic, and
              // location for the subject; camera is optional) can be admitted.
              // Earlier states (invited, link opened, setting up) are shown for
              // progress only — no premature Admit/Decline buttons.
              const inWaitingRoom = inv.status === "waiting_room";
              return (
                <Box
                  key={inv.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexWrap: "wrap",
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 1,
                    p: 1,
                    opacity: removed ? 0.55 : 1,
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 200 }}>
                    <Typography variant="body2">
                      <strong>{inv.name}</strong>
                      {inv.label ? ` (${inv.label})` : ""}{" "}
                      <Chip
                        size="small"
                        label={t(`roles.${inv.role}`, {
                          defaultValue: inv.role,
                        })}
                        sx={{ ml: 0.5 }}
                      />
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t("waitingRoom.openedAt", {
                        time: fmtTime(inv.link_opened_at),
                      })}
                      {inv.device_info
                        ? ` · ${inv.device_info.slice(0, 48)}`
                        : ""}
                    </Typography>
                  </Box>

                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <ReadyIcon
                      ok={!!inv.notice_acknowledged_at}
                      label={t("waitingRoom.readiness.notice")}
                      icon={<CheckCircle sx={{ fontSize: 16 }} />}
                    />
                    <ReadyIcon
                      ok={inv.camera_granted}
                      label={t("waitingRoom.readiness.camera")}
                      icon={<Videocam sx={{ fontSize: 16 }} />}
                    />
                    <ReadyIcon
                      ok={inv.microphone_granted}
                      label={t("waitingRoom.readiness.microphone")}
                      icon={<Mic sx={{ fontSize: 16 }} />}
                    />
                    <ReadyIcon
                      ok={inv.location_granted}
                      label={t("waitingRoom.readiness.location")}
                      icon={<LocationOn sx={{ fontSize: 16 }} />}
                    />
                  </Stack>

                  <Chip
                    size="small"
                    variant="outlined"
                    color={admitted ? "success" : "default"}
                    label={
                      STATUS_KEYS[inv.status]
                        ? t(STATUS_KEYS[inv.status])
                        : inv.status
                    }
                  />

                  {inWaitingRoom && (
                    <Button
                      size="small"
                      variant="contained"
                      disabled={acting === inv.id + "admit"}
                      onClick={() => decide(inv.id, "admit")}
                    >
                      {t("waitingRoom.admit")}
                    </Button>
                  )}
                  {(inWaitingRoom || admitted) && (
                    <Button
                      size="small"
                      color="error"
                      disabled={acting === inv.id + "remove"}
                      onClick={() =>
                        decide(inv.id, admitted ? "remove" : "decline")
                      }
                    >
                      {admitted
                        ? t("waitingRoom.remove")
                        : t("waitingRoom.decline")}
                    </Button>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        {located.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              {t("waitingRoom.locationsHeading")}
            </Typography>
            <ParticipantLocationsMap points={located} height={260} />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("waitingRoom.close")}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default WaitingRoomDialog;
